import type { ColumnFiltersState, Row } from '@tanstack/react-table';
import Decimal from 'decimal.js';
import type { DateRangeFilterValue, RangeFilterValue } from '@/components/table/table.types';
import { areDatesTheSameDay, formatDate } from './date.utils';
import { isMatchingFilter } from './string.utils';

const getColumnMeta = <TData>(row: Row<TData>, columnId: string) =>
  row.getAllCells().find((cell) => cell.column.id === columnId)?.column.columnDef.meta;

const getCellFilterValues = <TData>(row: Row<TData>, columnId: string): unknown[] => {
  const getFilterValue = getColumnMeta(row, columnId)?.getFilterValue;
  const rawValue = getFilterValue ? getFilterValue(row.original) : row.getValue(columnId);
  return Array.isArray(rawValue) ? rawValue : [rawValue];
};

export const multiValueFilter = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: unknown[]
): boolean => {
  if (!filterValue || filterValue.length === 0) return true;

  const meta = getColumnMeta(row, columnId);
  const cellValues = getCellFilterValues(row, columnId);

  const selectedByGroup = new Map<string | undefined, unknown[]>();
  filterValue.forEach((value) => {
    const group = meta?.options?.find((option) => option.value === value)?.group;
    selectedByGroup.set(group, [...(selectedByGroup.get(group) ?? []), value]);
  });
  return [...selectedByGroup.values()].every((selected) =>
    cellValues.some((cellValue) => selected.includes(cellValue))
  );
};

function valueToSearchableString(value: unknown): string {
  if (value == null) return '';

  if (Decimal.isDecimal(value)) return value.toString();

  if (value instanceof Date) return formatDate(value);

  return String(value);
}

export const createGlobalFilterWithFields = <TData>(
  extraSearchableFields?: (row: TData) => Array<string | null | undefined>
) => {
  return (row: Row<TData>, _: string, filterValue: string): boolean => {
    if (!filterValue) return true;

    const allCells = row.getAllCells().map((cell) => cell.getValue());
    const allFields = [
      ...allCells,
      ...(extraSearchableFields ? extraSearchableFields(row.original) : []),
    ].map(valueToSearchableString);

    // Each word may match a different column, so a full name finds its prénom + nom row.
    return filterValue
      .trim()
      .split(/\s+/)
      .every((word) => allFields.some((field) => isMatchingFilter(field, word)));
  };
};

export const createEnumSortingFn = <TData>(
  options?: { value: unknown; label: string }[]
): ((rowA: Row<TData>, rowB: Row<TData>, columnId: string) => number) => {
  if (!options) {
    return (rowA, rowB, columnId) =>
      String(rowA.getValue(columnId)).localeCompare(String(rowB.getValue(columnId)));
  }

  const labelsMap = new Map(options.map((opt) => [opt.value, opt.label]));

  return (rowA, rowB, columnId) => {
    const valueA = rowA.getValue(columnId);
    const valueB = rowB.getValue(columnId);

    const labelA = labelsMap.get(valueA) ?? String(valueA);
    const labelB = labelsMap.get(valueB) ?? String(valueB);

    return labelA.localeCompare(labelB);
  };
};

function toNumber(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (Decimal.isDecimal(value)) return value.toNumber();
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

export const numericRangeFilter = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: RangeFilterValue
): boolean => {
  if (!filterValue || (filterValue.min == null && filterValue.max == null)) return true;

  const rawValue = row.getValue(columnId);
  const cellValue = toNumber(rawValue);
  if (cellValue == null) return false;

  if (filterValue.min != null && cellValue < filterValue.min) return false;
  if (filterValue.max != null && cellValue > filterValue.max) return false;

  return true;
};

export const dateRangeFilter = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: DateRangeFilterValue
): boolean => {
  if (!filterValue || (filterValue.min == null && filterValue.max == null)) return true;

  const rawValue = row.getValue(columnId);
  if (!rawValue) return false;

  let cellDate: Date;
  if (rawValue instanceof Date) {
    cellDate = rawValue;
  } else if (typeof rawValue === 'string') {
    cellDate = new Date(rawValue);
  } else {
    return false;
  }

  if (Number.isNaN(cellDate.getTime())) return false;

  if (filterValue.min != null) {
    const minDate = new Date(filterValue.min);
    minDate.setHours(0, 0, 0, 0);
    if (cellDate < minDate) return false;
  }

  if (filterValue.max != null) {
    const maxDate = new Date(filterValue.max);
    maxDate.setHours(23, 59, 59, 999);
    if (cellDate > maxDate) return false;
  }

  return true;
};

function isRangeFilterValue(value: unknown): value is RangeFilterValue | DateRangeFilterValue {
  return (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    ('min' in value || 'max' in value)
  );
}

function isDateValue(value: unknown): boolean {
  if (value instanceof Date) return true;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return !Number.isNaN(parsed.getTime()) && value.includes('-');
  }
  return false;
}

// The DatePicker emits the picked day at UTC midnight, and a remembered filter comes back from
// storage as its ISO string. The API takes a bare calendar day: axios would send the Date as a full
// timestamp, which the API rejects with a 400 and the whole list fails to load.
function toCalendarDay(value: unknown): string | undefined {
  if (!(value instanceof Date) && typeof value !== 'string') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
}

export function columnFiltersToDateRange(
  filters: ColumnFiltersState,
  columnId: string
): { dateFrom?: string; dateTo?: string } {
  const filter = filters.find((f) => f.id === columnId);
  if (!filter) return {};
  const { min, max } = filter.value as { min?: unknown; max?: unknown };
  const dateFrom = toCalendarDay(min);
  const dateTo = toCalendarDay(max);
  return {
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
  };
}

export function columnFiltersToNumericRange(
  filters: ColumnFiltersState,
  columnId: string,
  minKey = 'amountMin',
  maxKey = 'amountMax'
): Record<string, number | undefined> {
  const filter = filters.find((f) => f.id === columnId);
  if (!filter) return {};
  const v = filter.value as { min?: number; max?: number };
  return {
    [minKey]: v.min,
    [maxKey]: v.max,
  };
}

export function columnFiltersToStringArray(
  filters: ColumnFiltersState,
  columnId: string
): string[] | undefined {
  const filter = filters.find((f) => f.id === columnId);
  if (!filter) return undefined;
  return filter.value as string[];
}

export const combinedFilter = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: unknown
): boolean => {
  if (filterValue == null) return true;

  // A bare Date means exact-day match (external DatePicker filters inject one)
  if (filterValue instanceof Date) {
    const rawValue = row.getValue(columnId);
    const cellDate =
      rawValue instanceof Date
        ? rawValue
        : typeof rawValue === 'string'
          ? new Date(rawValue)
          : null;
    if (!cellDate || Number.isNaN(cellDate.getTime())) return false;
    return areDatesTheSameDay(cellDate, filterValue);
  }

  if (isRangeFilterValue(filterValue)) {
    const rawValue = row.getValue(columnId);
    if (isDateValue(rawValue)) {
      return dateRangeFilter(row, columnId, filterValue as DateRangeFilterValue);
    }
    return numericRangeFilter(row, columnId, filterValue as RangeFilterValue);
  }

  if (Array.isArray(filterValue)) {
    return multiValueFilter(row, columnId, filterValue);
  }

  if (typeof filterValue === 'string') {
    if (!filterValue) return true;
    return getCellFilterValues(row, columnId).some((value) =>
      isMatchingFilter(valueToSearchableString(value), filterValue)
    );
  }

  return true;
};

export function columnFiltersToString(
  filters: ColumnFiltersState,
  columnId: string
): string | undefined {
  const value = filters.find((f) => f.id === columnId)?.value;
  return typeof value === 'string' && value !== '' ? value : undefined;
}
